import dayjs from 'dayjs';
import { Flex, Typography, theme } from 'antd';
import { formatCurrency } from '@/utils/currencyUtils';
import { themeRawValues } from '@/utils/styleUtils';
import { MonetaryAmount } from '@/types';
import { CampaignTypesTooltipContentEcho } from './CampaignTypesChart/CampaignTypesTooltipContentEcho';

/** Same shape as Decibel `useDateFormatter({ month: 'short', day: 'numeric' })` (Ant stack uses dayjs). */
const formatChartTooltipDate = (isoDate: string): string => dayjs(isoDate).format('MMM D');

const flexNonEmptyPlaceholder = '\u200b';
const CHART_TOOLTIP_MAX_WIDTH = 256;

interface BaseTooltipPayload {
  name: string;
  color: string;
  total: MonetaryAmount;
}

export interface CurrencyTooltipPayload extends BaseTooltipPayload {
  type: 'currency';
}

interface EarningsTooltipPayload extends BaseTooltipPayload {
  type: 'date/earnings';
  calculation_date: string;
  impressions: number;
}

export interface CampaignTypesChartTooltipPayload extends BaseTooltipPayload {
  type: 'campaign-types';
  impressions: number;
  percentage: number;
}

type TooltipPayload =
  | CurrencyTooltipPayload
  | EarningsTooltipPayload
  | CampaignTypesChartTooltipPayload;

interface ChartTooltipContentProps {
  payload: TooltipPayload | null;
  type: TooltipPayload['type'];
}

interface ChartTooltipWrapperProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Wrapper component for chart tooltips providing consistent styling
 */
export const ChartTooltipWrapperEcho = ({ children, className }: ChartTooltipWrapperProps) => {
  const { token } = theme.useToken();

  return (
    <Flex
      role="tooltip"
      align="center"
      className={className}
      style={{
        maxWidth: CHART_TOOLTIP_MAX_WIDTH,
        padding: token.paddingXS,
        borderRadius: token.borderRadius,
        background: token.colorBgLayout,
        color: token.colorText,
        boxShadow: token.boxShadow,
      }}
    >
      {children}
    </Flex>
  );
};

/**
 * Content component for chart tooltips displaying value and currency information
 */
export const ChartTooltipContentEcho = ({ payload, type }: ChartTooltipContentProps) => {
  const { token } = theme.useToken();

  if (!payload) return null;

  if (type === 'campaign-types') {
    const campaignPayload = payload as CampaignTypesChartTooltipPayload;
    return (
      <Flex
        vertical
        role="tooltip"
        style={{
          minWidth: 192,
          padding: token.paddingSM,
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorBgElevated,
          color: token.colorText,
        }}
      >
        <CampaignTypesTooltipContentEcho
          payload={{
            name: campaignPayload.name,
            total: campaignPayload.total,
            impressions: campaignPayload.impressions,
            percentage: campaignPayload.percentage,
          }}
        />
      </Flex>
    );
  }

  const { name, color, total } = payload;
  const chartColorMatch = /^chartColors-color(\d+)$/.exec(color);
  const chartPalette = themeRawValues.colors.chartColors;
  const paletteKey = chartColorMatch
    ? (`color${chartColorMatch[1]}` as keyof typeof chartPalette)
    : null;
  const legendDotFill = paletteKey
    ? (chartPalette[paletteKey] ?? chartPalette.color1)
    : color || chartPalette.color1;
  const legendDotSize = token.fontSize;

  return (
    <Flex
      vertical
      gap={token.marginXS}
      role="tooltip"
      style={{
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorPrimary}40`,
        background: token.colorText,
        padding: token.paddingSM,
        color: token.colorTextLightSolid,
      }}
    >
      {type === 'currency' && (
        <Flex align="center" gap={token.marginXS}>
          <Flex
            flex="none"
            aria-hidden
            justify="center"
            align="center"
            style={{
              width: legendDotSize,
              height: legendDotSize,
              borderRadius: '50%',
              backgroundColor: legendDotFill,
              overflow: 'hidden',
            }}
          >
            {flexNonEmptyPlaceholder}
          </Flex>
          <Typography.Paragraph
            style={{ margin: 0, marginInlineEnd: token.marginLG, textTransform: 'capitalize' }}
          >
            {name}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ margin: 0, color: token.colorTextLightSolid }}>
            {formatCurrency(total?.amount || 0, total?.currency)}
          </Typography.Paragraph>
        </Flex>
      )}
      {type === 'date/earnings' && (payload as EarningsTooltipPayload).calculation_date && (
        <Flex vertical gap={token.marginXS}>
          <Typography.Paragraph
            style={{
              margin: 0,
              marginInlineEnd: token.marginLG,
              textTransform: 'capitalize',
              color: token.colorTextLightSolid,
            }}
          >
            {formatChartTooltipDate((payload as EarningsTooltipPayload).calculation_date)}
          </Typography.Paragraph>
          <Flex align="center" gap={token.marginXS}>
            <Flex
              flex="none"
              aria-hidden
              justify="center"
              align="center"
              style={{
                width: legendDotSize,
                height: legendDotSize,
                borderRadius: '50%',
                backgroundColor: legendDotFill,
                overflow: 'hidden',
              }}
            >
              {flexNonEmptyPlaceholder}
            </Flex>
            <Typography.Paragraph style={{ margin: 0, color: token.colorTextLightSolid }}>
              Estimated earnings:{' '}
              <Typography.Text strong style={{ color: token.colorTextLightSolid }}>
                {formatCurrency(total?.amount || 0, total?.currency, true)}
              </Typography.Text>
            </Typography.Paragraph>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
